import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet } from 'react-router-dom';
import { api } from '../api/client';
import type { AttentionRow, DashboardTotals, DemoStatus } from '../api/types';
import { useAuth, useCapabilities } from '../lib/auth';
import { useTheme } from '../lib/theme';
import {
  HeronMark,
  IconAudit,
  IconCompanies,
  IconDashboard,
  IconLicense,
  IconLogout,
  IconMail,
  IconMoon,
  IconProducts,
  IconRefresh,
  IconShield,
  IconSun,
  IconSync,
} from './icons';
import { Button } from './ui';

const NAV = [
  { to: '/', label: 'Overview', icon: IconDashboard, end: true },
  { to: '/companies', label: 'Companies', icon: IconCompanies },
  { to: '/licenses', label: 'Licenses', icon: IconLicense },
  // Support is read-only on just the three pages above; everything below is
  // hidden for them (and refused by the server too).
  { to: '/products', label: 'Products', icon: IconProducts, writerOnly: true },
  { to: '/sources', label: 'Sources', icon: IconSync, writerOnly: true },
];

const NAV_RECORDS = [
  { to: '/audit-logs', label: 'Activity', icon: IconAudit },
  { to: '/email-outbox', label: 'Sent email', icon: IconMail },
];

export function AppShell() {
  const { staff, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const can = useCapabilities();
  const nav = NAV.filter((item) => can.canWrite || !item.writerOnly);

  // The licenses nav item carries a count of what is broken right now, so a
  // staff member who signs in to do something else still sees an outage.
  const { data } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () =>
      api.get<{ totals: DashboardTotals; attention: AttentionRow[] }>(
        '/api/staff/licenses/summary/dashboard',
      ),
    refetchInterval: 120_000,
  });

  const expired = data?.totals.expired_licenses ?? 0;

  // The demo section is hidden entirely rather than shown disabled: on a real
  // production estate it is not a feature that is switched off, it is a
  // feature that does not exist.
  const { data: demo } = useQuery({
    queryKey: ['demo-status'],
    queryFn: () => api.get<DemoStatus>('/api/staff/demo/status'),
    staleTime: 300_000,
    // Support cannot reach the demo endpoints; don't fire a request that 403s.
    enabled: can.canWrite,
  });

  return (
    <div className="shell">
      <div className="shell__brand">
        <HeronMark size={22} />
        <span>Heron License</span>
      </div>

      <header className="shell__bar">
        <span className="small faint">
          {/* Names the estate being administered, so a staging window is not
              mistaken for production. */}
          {window.location.hostname === 'localhost' ? 'Local' : window.location.hostname}
        </span>
        <div className="row" style={{ gap: 'var(--s3)' }}>
          <button
            type="button"
            className="icon-btn"
            onClick={toggle}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {theme === 'dark' ? <IconSun /> : <IconMoon />}
          </button>
          <span className="small muted nowrap">{staff?.name ?? staff?.email}</span>
          <Button size="sm" variant="ghost" icon={<IconLogout />} onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </header>

      <nav className="shell__nav scroll-area" aria-label="Sections">
        {nav.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} className="navlink">
            <item.icon />
            <span>{item.label}</span>
            {item.to === '/licenses' && expired > 0 && (
              <span className="navlink__count" title={`${expired} expired license(s)`}>
                {expired}
              </span>
            )}
          </NavLink>
        ))}

        {can.canWrite && (
          <>
            <div className="nav__group">Records</div>
            {NAV_RECORDS.map((item) => (
              <NavLink key={item.to} to={item.to} className="navlink">
                <item.icon />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}

        {can.isAdmin && (
          <>
            <div className="nav__group">Admin</div>
            <NavLink to="/staff" className="navlink">
              <IconShield size={18} />
              <span>Staff</span>
            </NavLink>
          </>
        )}

        {demo?.enabled && (
          <>
            <div className="nav__group">Sandbox</div>
            <NavLink to="/demo-data" className="navlink">
              <IconRefresh size={18} />
              <span>Demo data</span>
            </NavLink>
          </>
        )}
      </nav>

      <main className="shell__main">
        <div className="shell__inner">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
